// store/modules/analytics.js
import api from '@/api/api'

export default {
    namespaced: true,
    state: {
        metrics: {
            talentCount: 0,
            requestCount: 0,
            shortlistedCount: 0,
            conversionRate: 0,
            talentGrowth: 0,
            requestGrowth: 0,
            shortlistGrowth: 0,
            conversionGrowth: 0
        },
        applicationStatus: [],
        applicationTrends: [],
        skillsDistribution: [],
        geoDistribution: {
            countries: [],
            cities: []
        },
        salaryDistribution: {
            ranges: [],
            distribution: []
        },
        topSkills: [],
        recentApplications: [],
        adminPerformance: {
            summary: {
                topPerformer: null,
                totalReferrals: 0,
                overallConversionRate: 0,
                activeRecruiters: 0
            },
            adminPerformance: [],
            trendData: {
                labels: [],
                datasets: []
            }
        },
        loading: false,
        error: null
    },
    getters: {
        metrics: (state) => state.metrics,
        applicationStatus: (state) => state.applicationStatus,
        applicationTrends: (state) => state.applicationTrends,
        skillsDistribution: (state) => state.skillsDistribution,
        geoDistribution: (state) => state.geoDistribution,
        salaryDistribution: (state) => state.salaryDistribution,
        topSkills: (state) => state.topSkills,
        recentApplications: (state) => state.recentApplications,
        adminPerformance: (state) => state.adminPerformance,
        loading: (state) => state.loading,
        error: (state) => state.error
    },
    mutations: {
        SET_METRICS(state, metrics) {
            state.metrics = metrics;
        },
        SET_APPLICATION_STATUS(state, data) {
            state.applicationStatus = data;
        },
        SET_APPLICATION_TRENDS(state, data) {
            state.applicationTrends = data;
        },
        SET_SKILLS_DISTRIBUTION(state, data) {
            state.skillsDistribution = data;
        },
        SET_GEO_DISTRIBUTION(state, data) {
            state.geoDistribution = data;
        },
        SET_SALARY_DISTRIBUTION(state, data) {
            state.salaryDistribution = data;
        },
        SET_TOP_SKILLS(state, data) {
            state.topSkills = data;
        },
        SET_RECENT_APPLICATIONS(state, data) {
            state.recentApplications = data;
        },
        SET_ADMIN_PERFORMANCE(state, data) {
            state.adminPerformance = data;
        },
        SET_LOADING(state, status) {
            state.loading = status;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        CLEAR_ERROR(state) {
            state.error = null;
        }
    },
    actions: {
        // Fetch dashboard metrics
        async fetchMetrics({ commit }, dateRange = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/metrics', { params: dateRange });
                commit('SET_METRICS', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch analytics metrics';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch application status data
        async fetchApplicationStatus({ commit }, dateRange = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/application-status', { params: dateRange });
                commit('SET_APPLICATION_STATUS', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch application status data';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch application trends data
        async fetchApplicationTrends({ commit }, params = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/application-trends', { params });
                commit('SET_APPLICATION_TRENDS', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch application trends data';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch skills distribution data
        async fetchSkillsDistribution({ commit }, params = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/skills-distribution', { params });
                commit('SET_SKILLS_DISTRIBUTION', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch skills distribution data';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch geographic distribution data
        async fetchGeoDistribution({ commit }, params = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/geo-distribution', { params });
                commit('SET_GEO_DISTRIBUTION', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch geographic distribution data';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch salary distribution data
        async fetchSalaryDistribution({ commit }, params = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/salary-distribution', { params });
                commit('SET_SALARY_DISTRIBUTION', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch salary distribution data';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch top skills data
        async fetchTopSkills({ commit }, params = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/top-skills', { params });
                commit('SET_TOP_SKILLS', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch top skills data';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch recent applications data
        async fetchRecentApplications({ commit }, params = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/recent-applications', { params });
                commit('SET_RECENT_APPLICATIONS', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch recent applications data';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch admin performance data (SUPERADMIN ONLY)
        async fetchAdminPerformance({ commit }, params = {}) {
            try {
                commit('SET_LOADING', true);
                commit('CLEAR_ERROR');

                const response = await api.get('/analytics/admin-performance', { params });
                commit('SET_ADMIN_PERFORMANCE', response.data.data);

                return response.data.data;
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Failed to fetch admin performance data';
                commit('SET_ERROR', errorMessage);
                throw new Error(errorMessage);
            } finally {
                commit('SET_LOADING', false);
            }
        },

        // Fetch all analytics data at once
        async fetchAllAnalyticsData({ dispatch }, dateRange = {}) {
            try {
                await Promise.all([
                    dispatch('fetchMetrics', dateRange),
                    dispatch('fetchApplicationStatus', dateRange),
                    dispatch('fetchApplicationTrends', {
                        timeframe: dateRange.timeframe || 'monthly',
                        ...dateRange
                    }),
                    dispatch('fetchSkillsDistribution', {
                        limit: dateRange.topSkillsCount || 10,
                        ...dateRange
                    }),
                    dispatch('fetchGeoDistribution', dateRange),
                    dispatch('fetchSalaryDistribution', dateRange),
                    dispatch('fetchTopSkills', {
                        limit: dateRange.topSkillsCount || 20,
                        ...dateRange
                    }),
                    dispatch('fetchRecentApplications', {
                        limit: 20,
                        ...dateRange
                    })
                ]);

                return true;
            } catch (error) {
                console.error('Error fetching all analytics data:', error);
                throw error;
            }
        },

        // Fetch all analytics data including admin performance (for superadmins)
        async fetchAllAnalyticsDataWithAdminPerformance({ dispatch }, dateRange = {}) {
            try {
                await Promise.all([
                    dispatch('fetchAllAnalyticsData', dateRange),
                    dispatch('fetchAdminPerformance', dateRange)
                ]);

                return true;
            } catch (error) {
                console.error('Error fetching all analytics data with admin performance:', error);
                throw error;
            }
        }
    }
}
